import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  CreateAxiosDefaults,
} from 'axios';
import type { SupabaseClient } from '@dopomogai/supabase-client';

type HeaderMap = Record<string, string>;

const g = globalThis as any;
g.__DG_API__ = g.__DG_API__ || {
  axios: undefined as AxiosInstance | undefined,
  base: '',
  configured: false,

  // auth wiring (preferred)
  headerProvider: null as null | (() => Promise<HeaderMap>),
  refreshFn: null as null | (() => Promise<string>),
  onTokenRefreshed: null as null | ((token: string) => void),

  // refresh queue
  isRefreshing: false,
  pendingResolvers: [] as Array<() => void>,

  // optional platform auth
  supabase: undefined as SupabaseClient | undefined,
};

export interface ApiClientConfig {
  apiBaseUrl: string;

  /**
   * Optional.
   * PLATFORM apps can pass Supabase to enable automatic refreshSession() on 401
   * if refreshFn is not provided.
   */
  supabaseClient?: SupabaseClient;

  timeoutMs?: number;

  /**
   * Optional legacy behavior (avoid if possible to reduce calls):
   * If true, tries supabase.auth.getSession() (with timeout) when no headerProvider.
   *
   * Default:
   * - true if supabaseClient exists AND no headerProvider has been set
   * - false otherwise
   */
  attachSupabaseSessionOnRequest?: boolean;
}

// -----------------------------
// Debug helpers
// -----------------------------
function dbgEnabled() {
  return typeof window !== 'undefined' && !!(window as any).__DG_DEBUG;
}
function dbg(...args: any[]) {
  if (dbgEnabled()) console.debug(...args);
}
function dbgWarn(...args: any[]) {
  if (dbgEnabled()) console.warn(...args);
}

// -----------------------------
// Axios header utilities (fixes TS errors)
// -----------------------------
function asAxiosHeaders(h?: AxiosRequestConfig['headers']): AxiosHeaders {
  // Axios v1 types allow headers to be AxiosHeaders | RawAxiosHeaders | string[] | undefined
  // We normalize to AxiosHeaders so TS is happy and runtime is consistent.
  if (h instanceof AxiosHeaders) return h;
  return new AxiosHeaders((h || {}) as any);
}

function setHeader(config: AxiosRequestConfig, key: string, value: string) {
  const headers = asAxiosHeaders(config.headers);
  headers.set(key, value);
  config.headers = headers;
}

function deleteHeader(config: AxiosRequestConfig, key: string) {
  const headers = asAxiosHeaders(config.headers);
  headers.delete(key);
  config.headers = headers;
}

function hasHeader(config: AxiosRequestConfig, key: string): boolean {
  const headers = asAxiosHeaders(config.headers);
  return headers.has(key);
}

function mergeHeaders(config: AxiosRequestConfig, extra: HeaderMap) {
  const headers = asAxiosHeaders(config.headers);
  for (const [k, v] of Object.entries(extra || {})) {
    if (v != null) headers.set(k, v);
  }
  config.headers = headers;
}

// -----------------------------
// Supabase helpers
// -----------------------------
async function getSessionWithTimeout(supabase: SupabaseClient, ms = 1200) {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error('getSession timeout')), ms),
  );
  return Promise.race([supabase.auth.getSession(), timeout]);
}

async function refreshSupabaseToken(supabase: SupabaseClient): Promise<string> {
  const { data, error } = await supabase.auth.refreshSession();
  if (error) throw error;
  const token = data?.session?.access_token;
  if (!token) throw new Error('Supabase refreshSession returned no access_token');
  return token;
}

// -----------------------------
// Public wiring API (preferred path)
// -----------------------------
export function setAuthHeaderProvider(fn: () => Promise<HeaderMap>) {
  g.__DG_API__.headerProvider = fn;
}

export function setAuthRefresh(fn: () => Promise<string>, onRefreshed?: (token: string) => void) {
  g.__DG_API__.refreshFn = fn;
  g.__DG_API__.onTokenRefreshed = onRefreshed || null;
}

// -----------------------------
// Configure client
// -----------------------------
export function configureApiClient(cfg: ApiClientConfig) {
  if (g.__DG_API__.configured) return;

  if (!cfg.apiBaseUrl) throw new Error('API Base URL is required.');

  const base = cfg.apiBaseUrl.replace(/\/$/, '');
  const axiosDefaults: CreateAxiosDefaults = {
    baseURL: `${base}/api/v1`,
    timeout: cfg.timeoutMs ?? 120000,
  };

  const instance = axios.create(axiosDefaults);

  g.__DG_API__.axios = instance;
  g.__DG_API__.base = base;
  g.__DG_API__.supabase = cfg.supabaseClient;

  const shouldAttachSupabaseSessionOnRequest =
    cfg.attachSupabaseSessionOnRequest ??
    (!!cfg.supabaseClient && !g.__DG_API__.headerProvider);

  // Request: attach auth headers
  instance.interceptors.request.use(async (config) => {
    dbg('[api:req]', config.method?.toUpperCase(), config.url, {
      focus: typeof document !== 'undefined' ? document.visibilityState : 'n/a',
      t: Date.now(),
    });

    // Respect caller auth header (WAT/API keys etc.)
    if (hasHeader(config, 'Authorization') || hasHeader(config, 'authorization')) return config;

    // Preferred: headerProvider (wired by ChatProvider)
    const hp: null | (() => Promise<HeaderMap>) = g.__DG_API__.headerProvider || null;
    if (hp) {
      try {
        const provided = await hp();
        mergeHeaders(config, provided);
        dbg('[api:req:setAuth:provider]', config.url);
        return config;
      } catch (e) {
        dbgWarn('[api:req:headerProvider:failed]', config.url, e);
        // continue to fallback
      }
    }

    // Legacy fallback: supabase getSession() per request (avoid if you care about extra calls)
    if (shouldAttachSupabaseSessionOnRequest && cfg.supabaseClient) {
      try {
        const res: any = await getSessionWithTimeout(cfg.supabaseClient, 1200);
        const token = res?.data?.session?.access_token;
        if (token) {
          setHeader(config, 'Authorization', `Bearer ${token}`);
          dbg('[api:req:setAuth:supabaseGetSession]', config.url);
        } else {
          dbg('[api:req:noAuthToken]', config.url);
        }
      } catch (e) {
        dbgWarn('[api:req:getSession:timeout]', config.url, e);
      }
    }

    // Always ensure JSON content-type for non-FormData when not set.
    // (We do NOT force it on FormData uploads.)
    const ct = asAxiosHeaders(config.headers).get('Content-Type');
    const isFormData =
      typeof FormData !== 'undefined' && config.data instanceof FormData;

    if (!ct && !isFormData) {
      setHeader(config, 'Content-Type', 'application/json');
    }

    return config;
  });

  // Response: 401 refresh queue
  instance.interceptors.response.use(
    (res) => {
      dbg('[api:res]', res.config?.url, res.status);
      return res;
    },
    async (err: AxiosError) => {
      dbgWarn('[api:res:err]', (err as any)?.config?.url, err?.response?.status, err?.message);

      const status = err.response?.status;
      const original = err.config as any;

      if (status !== 401) return Promise.reject(err);

      // Prevent infinite retry loops
      if (original && original.__retry) return Promise.reject(err);

      const customRefresh: null | (() => Promise<string>) = g.__DG_API__.refreshFn || null;
      const supabase: SupabaseClient | undefined = g.__DG_API__.supabase;

      // No refresh path => hard fail (prevents storms)
      if (!customRefresh && !supabase) return Promise.reject(err);

      // Queue behind a single refresh
      if (g.__DG_API__.isRefreshing) {
        await new Promise<void>((resolve) => g.__DG_API__.pendingResolvers.push(resolve));
      } else {
        g.__DG_API__.isRefreshing = true;

        try {
          const newToken = customRefresh
            ? await customRefresh()
            : await refreshSupabaseToken(supabase!);

          try {
            const cb: null | ((t: string) => void) = g.__DG_API__.onTokenRefreshed || null;
            cb?.(newToken);
          } catch {}

          // Best-effort set default bearer header for subsequent requests.
          // If you use headerProvider, that remains source of truth.
          instance.defaults.headers.common.Authorization = `Bearer ${newToken}`;

          dbg('[api:401:refreshed]');
        } catch (refreshErr) {
          // Release waiters so they fail instead of hanging
          g.__DG_API__.pendingResolvers.splice(0).forEach((r: any) => r());
          g.__DG_API__.isRefreshing = false;
          dbgWarn('[api:401:refreshFailed]', refreshErr);
          return Promise.reject(err);
        }

        g.__DG_API__.pendingResolvers.splice(0).forEach((r: any) => r());
        g.__DG_API__.isRefreshing = false;
      }

      // Retry once; remove stale Authorization so request interceptor can re-attach fresh one.
      original.__retry = true;
      try {
        deleteHeader(original, 'Authorization');
        deleteHeader(original, 'authorization');
      } catch {}

      return instance(original);
    },
  );

  g.__DG_API__.configured = true;

  if (typeof window !== 'undefined') (window as any).__DG_API_AXIOS = instance;
  // eslint-disable-next-line no-console
  console.log('@dopomogai/api-client: configured with baseURL:', instance.defaults.baseURL);
}

export function getApiClient(): AxiosInstance {
  const inst = g.__DG_API__.axios;
  if (!inst) throw new Error('API Client not configured. Call configureApiClient() first.');
  return inst;
}

export function getRawApiBaseUrl(): string {
  const base = g.__DG_API__.base;
  if (!base) throw new Error('API Client not configured. Call configureApiClient() first.');
  return base;
}